import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useState, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import { formatLagos, lagosParts } from "../utils/tripLog";

// Days, weeks and months are Lagos calendar ones, whatever zone the admin's computer is in.
const filterBookingsByDateRange = (bookings, range) => {
  if (!bookings || bookings.length === 0) return [];

  const today = lagosParts(new Date());

  return bookings.filter((booking) => {
    const booked = lagosParts(booking.createdAt);
    if (!booked) return false;

    switch (range) {
      case "Daily":
        return booked.dayNumber === today.dayNumber;
      case "Weekly": {
        // Sunday to Saturday.
        const weekStart = today.dayNumber - today.weekday;
        return booked.dayNumber >= weekStart && booked.dayNumber < weekStart + 7;
      }
      case "Monthly":
        return booked.month === today.month && booked.year === today.year;
      default:
        return true;
    }
  });
};

// Categorise a single booking
const getBookingCategory = (booking) => {
  if (booking.isCharter) return "chartered";
  if (booking.isScheduled) return "scheduled";
  return "booked";
};

// Group bookings into time-slot buckets
const groupBookingsByTime = (bookings, range) => {
  const filtered = filterBookingsByDateRange(bookings, range);
  if (filtered.length === 0) return [];

  const grouped = {};

  filtered.forEach((booking) => {
    const parts = lagosParts(booking.createdAt);
    let label;

    if (range === "Daily") {
      label = `${parts.hour.toString().padStart(2, "0")}:00`;
    } else if (range === "Weekly") {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      label = days[parts.weekday];
    } else {
      // Monthly — group by day of month
      const month = formatLagos(booking.createdAt, "default", { month: "short" });
      label = `${parts.day} ${month}`;
    }

    if (!grouped[label]) {
      grouped[label] = { time: label, chartered: 0, booked: 0, scheduled: 0 };
    }

    const category = getBookingCategory(booking);
    grouped[label][category]++;
  });

  return Object.values(grouped).sort((a, b) => {
    if (range === "Daily") return a.time.localeCompare(b.time);
    if (range === "Weekly") {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return days.indexOf(a.time) - days.indexOf(b.time);
    }
    return parseInt(a.time) - parseInt(b.time);
  });
};

export default function FleetPerformanceCard({ bookings = [], loading = false, error = null, onRetry }) {
  const [timeRange, setTimeRange] = useState("Daily");
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Theme variables
  const bgColor = isDark ? "bg-gray-900" : "bg-white";
  const textColor = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-600";
  const gridColor = isDark ? "#374151" : "#e5e7eb";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const borderColor = isDark ? "border-gray-700" : "border-gray-300";
  const selectBg = isDark ? "bg-gray-800" : "bg-white";
  const selectText = isDark ? "text-white" : "text-gray-900";

  const chartData = useMemo(
    () => groupBookingsByTime(bookings, timeRange),
    [bookings, timeRange]
  );


  return (
    <div className={`${bgColor} p-4 transition-colors !border-none !shadow-none`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-lg font-semibold ${textColor}`}>Fleet Performance</h2>
        <div className="flex items-center space-x-2">
          <span className={`text-sm ${textSecondary}`}>View:</span>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className={`border ${borderColor} rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${selectBg} ${selectText} transition-colors`}
          >
            <option value="Daily">Daily</option>
            <option value="Weekly">This Week</option>
            <option value="Monthly">This Month</option>
          </select>
        </div>
      </div>

      {/* Legend */}
      <div className={`flex gap-4 mb-2 text-sm ${textColor}`}>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
          Chartered Ride
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-orange-500"></span>
          Booked Seat
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-purple-500"></span>
          Scheduled Ride
        </div>
      </div>

      {/* Chart */}
      <div className="h-64 w-full bg-transparent">
        {error ? (
          <div className="flex items-center justify-center h-full text-sm text-red-600">
            {"Couldn't load ride data. "}
            {onRetry ? (
              <button type="button" onClick={onRetry} className="underline ml-1">
                Retry
              </button>
            ) : null}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse text-gray-400">Loading chart data...</div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400">No data available for the selected period</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorChartered" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorBooked" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F97316" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorScheduled" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
              <XAxis dataKey="time" stroke={axisColor} tick={{ fontSize: 12 }} />
              <YAxis stroke={axisColor} tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? "#1f2937" : "#ffffff",
                  border: `1px solid ${borderColor}`,
                  borderRadius: "0.5rem",
                }}
                wrapperStyle={{ backgroundColor: "transparent", border: "none", boxShadow: "none" }}
              />
              <Area
                type="monotone"
                dataKey="chartered"
                stackId="1"
                stroke="#F59E0B"
                fill="url(#colorChartered)"
                name="Chartered"
              />
              <Area
                type="monotone"
                dataKey="booked"
                stackId="1"
                stroke="#F97316"
                fill="url(#colorBooked)"
                name="Booked"
              />
              <Area
                type="monotone"
                dataKey="scheduled"
                stackId="1"
                stroke="#8B5CF6"
                fill="url(#colorScheduled)"
                name="Scheduled"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}